import {
  Claim,
  PolicyType,
  PolicyTypeCd,
} from '@aaa-ncnu-ie/mypolicy-api-customers';
import { Stack, Card, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import CardHeader from '../../../../components/CardHeader';
import Button from '../../../../components/styledComponents/Button';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';
import theme from '../../../../styles/theme';
import { sentenceCase } from '../../../../utils/helpers';

interface ClaimCardProps {
  claim: Claim;
}

const ClaimCard = ({ claim }: ClaimCardProps) => {
  const {
    claimNumber,
    claimDetails,
    claimStatus,
    claimLossCause,
    policyTypeCd,
    policyNumber,
    fullProductCode,
  } = claim;
  const { track } = useAnalytics();

  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  let policyType = '';

  switch (policyTypeCd) {
    case 'HO':
      policyType = PolicyType.Property;
      break;
    case 'PA':
      policyType = PolicyType.AUTO;
      break;
    case 'PU':
      policyType = PolicyType.PUP;
      break;
    default:
      policyType = '';
  }

  const handleViewDetailsClick = () => {
    track('Claims Details Presented', {
      event_type: 'Link Accessed',
      location: 'Feature Card',
      link: 'View details',
      claim_number: claimNumber,
      claim_type: policyType,
      claim_status: claimStatus,
      policy_number: policyNumber,
      state: policyNumber.slice(0, 2),
      product_type: policyTypeCd,
    });
  };

  const CardButtons = () => (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      sx={{
        alignItems: "center",
        ml: 0,
        my: { mobile: 2, tablet: 0 }
      }}>
      <Button
        component={RouterLink}
        to={claimNumber}
        role="link"
        variant="contained"
        sx={{
          whiteSpace: 'nowrap',
        }}
        data-testid={`view-claim-details-${claimNumber}`}
        onClick={handleViewDetailsClick}
      >
        View details
      </Button>
    </Stack>
  );

  return (
    <Card
      component={Stack}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: { tablet: 5, mobile: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <CardHeader
        headerText={`Claim ${claimNumber}`}
        policyTypeCd={policyTypeCd as PolicyTypeCd}
        vehicles={
          claimDetails?.insuredVehicle && [claimDetails?.insuredVehicle]
        }
        insuredAddress={claimDetails?.insuredAddress}
        claimStatus={claimStatus}
        policyNumber={claimNumber}
        fullProductCode={fullProductCode}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          mt: { mobile: 2, tablet: 3 }
        }}>
        <Stack direction="column">
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { desktop: 44, tablet: 36, mobile: 33 },
              lineHeight: '57px',
              color: colors.csaaBlack,
            }}
            data-testid={`claim-title-${claimNumber}`}
          >
            {sentenceCase(claimLossCause) ?? `${policyType} Claim`}
          </Typography>
        </Stack>
        {!isMobile && <CardButtons />}
      </Stack>
      {isMobile && <CardButtons />}
    </Card>
  );
};

export default ClaimCard;
